/**
 * Environment variable validation and access
 * Validates required environment variables at startup
 */

interface EnvConfig {
  VITE_SUPABASE_URL: string
  VITE_SUPABASE_ANON_KEY: string
  VITE_ENABLE_BIG_IMPORT?: string
}

class EnvValidationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'EnvValidationError'
  }
}

/**
 * Validates that all required environment variables are present
 * Throws an error if any are missing
 */
export function validateEnv(): EnvConfig {
  const missing: string[] = []
  
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  
  if (!supabaseUrl) {
    missing.push('VITE_SUPABASE_URL')
  }
  
  if (!supabaseAnonKey) {
    missing.push('VITE_SUPABASE_ANON_KEY')
  }
  
  if (missing.length > 0) {
    throw new EnvValidationError(
      `Missing required environment variables: ${missing.join(', ')}\n\n` +
      `Please create a .env file in the root directory with the required variables.\n` +
      `See env.example for reference.`
    )
  }
  
  return {
    VITE_SUPABASE_URL: supabaseUrl,
    VITE_SUPABASE_ANON_KEY: supabaseAnonKey,
    VITE_ENABLE_BIG_IMPORT: import.meta.env.VITE_ENABLE_BIG_IMPORT
  }
}

/**
 * Get environment variable with optional fallback
 */
export function getEnvVar(key: string, fallback?: string): string {
  const value = import.meta.env[key]
  
  if (value === undefined && fallback === undefined) {
    // Use logger if available, otherwise silent (env.ts is loaded early)
    if (typeof window !== 'undefined' && (window as any).__logger) {
      (window as any).__logger.warn(`Environment variable ${key} is not set`)
    }
    return ''
  }
  
  return value || fallback || ''
}

/**
 * Get boolean environment variable
 */
export function getEnvBool(key: string, defaultValue: boolean = false): boolean {
  const value = import.meta.env[key]
  
  if (value === undefined) {
    return defaultValue
  }
  
  return value === 'true' || value === '1'
}

/**
 * Get number environment variable
 */
export function getEnvNumber(key: string, defaultValue?: number): number {
  const value = import.meta.env[key]
  
  if (value === undefined) {
    if (defaultValue === undefined) {
      throw new EnvValidationError(`Environment variable ${key} is required but not set`)
    }
    return defaultValue
  }
  
  const parsed = Number(value)
  
  if (isNaN(parsed)) {
    throw new EnvValidationError(`Environment variable ${key} must be a number, got: ${value}`)
  }
  
  return parsed
}

